import React, { useEffect, useMemo, useRef, useState, Fragment } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import DataTable from "datatables.net-dt";
import "datatables.net-dt/css/dataTables.dataTables.css";
import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/24/outline";

/* ----------------- Metrics ----------------- */
const METRICS = [
  { key: "ALL", label: "All metrics" },
  { key: "O2", label: "O₂ (%)" },
  { key: "CO", label: "CO (ppm)" },
  { key: "SO2", label: "SO₂ (ppm)" },
  { key: "NOX", label: "NOx (ppm)" },
  { key: "PARTICULATE", label: "Particulate" },
  { key: "FLOWRATE", label: "Flowrate" },
  { key: "OPACITY", label: "Opacity" },
  { key: "TEMPERATURE", label: "Temperature (°C)" },
  { key: "PRESSURE", label: "Pressure" },
];

const RANGE_OPTS = [
  { value: "24h", label: "Last 24 hours" },
  { value: "7d", label: "Last 7 days" },
];

/* ----------------- Helpers ----------------- */
function daysAgo(days, h = 0, m = 0) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - days);
  d.setHours(h, m, 0, 0);
  return d.getTime();
}

const SHAPE = {
  O2: { base: 20.5, amp: 1.2 },
  CO: { base: 320, amp: 90 },
  SO2: { base: 40, amp: 12 },
  NOX: { base: 110, amp: 25 },
  PARTICULATE: { base: 5, amp: 2 },
  FLOWRATE: { base: 30, amp: 6 },
  OPACITY: { base: 2, amp: 0.8 },
  TEMPERATURE: { base: 150, amp: 18 },
  PRESSURE: { base: 1.05, amp: 0.15 },
};

function seedFromKey(key) {
  return key.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
}
function valueFor(key, ts) {
  const shape = SHAPE[key];
  if (!shape) return 0;
  const s = seedFromKey(key);
  const v = shape.base + shape.amp * (0.5 + 0.5 * Math.sin(ts / 3.6e6 + s));
  return Number(Math.max(0, v).toFixed(2));
}

/* ---------- Fancy Tailwind Dropdown (Headless UI) ---------- */
function FancySelect({ value, onChange, options, className = "" }) {
  const selected =
    options.find((o) => o.value === value) || options[0] || { label: "", value: "" };

  return (
    <Listbox value={value} onChange={onChange}>
      <div className={`relative ${className}`}>
        <Listbox.Button
          className="relative w-52 cursor-pointer rounded-md border border-gray-300 bg-white pl-3 pr-10 py-2
                     text-left text-sm text-gray-800 shadow-sm transition
                     hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <span className="block truncate">{selected.label}</span>
          <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
            <ChevronUpDownIcon className="h-5 w-5 text-gray-500" />
          </span>
        </Listbox.Button>

        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
          enter="transition ease-out duration-150"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
        >
          <Listbox.Options
            className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none"
          >
            {options.map((opt) => (
              <Listbox.Option
                key={opt.value}
                value={opt.value}
                className={({ active }) =>
                  `relative cursor-pointer select-none py-2 pl-9 pr-3 ${
                    active ? "bg-blue-50 text-blue-700" : "text-gray-800"
                  }`
                }
              >
                {({ selected: isSel }) => (
                  <>
                    {isSel ? (
                      <span className="absolute inset-y-0 left-2 flex items-center text-blue-600">
                        <CheckIcon className="h-5 w-5" />
                      </span>
                    ) : null}
                    <span className={`block truncate ${isSel ? "font-medium" : "font-normal"}`}>
                      {opt.label}
                    </span>
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
}

/* ----------------- Main ----------------- */
export default function OneWeekSpline() {
  const [metric, setMetric] = useState("ALL");
  const [range, setRange] = useState("7d");

  const rangeDays = useMemo(() => (range === "24h" ? 1 : 7), [range]);

  const timepoints = useMemo(() => {
    const now = new Date();
    now.setMinutes(0, 0, 0);
    const stepHours = 3;
    const total = Math.floor((rangeDays * 24) / stepHours);
    const pts = [];
    for (let i = total; i >= 0; i--) {
      const t = new Date(now.getTime() - i * stepHours * 3600 * 1000);
      const jitterMins = (i * 7) % 50;
      t.setMinutes((t.getMinutes() + jitterMins) % 60);
      pts.push(t.getTime());
    }
    return pts;
  }, [rangeDays]);

  const activeMetrics = useMemo(
    () => METRICS.filter((m) => m.key !== "ALL" && (metric === "ALL" || m.key === metric)),
    [metric]
  );

  // Cards
  const latestTs = timepoints[timepoints.length - 1] ?? Date.now();
  const latestCards = (metric === "ALL" ? METRICS.filter(m => m.key !== "ALL") : activeMetrics).map(
    (m) => ({ key: m.key, label: m.label, value: valueFor(m.key, latestTs) })
  );

  // Chart
  const series =
    metric === "ALL"
      ? METRICS.filter((m) => m.key !== "ALL").map((m) => ({
          name: m.label,
          data: timepoints.map((t) => [t, valueFor(m.key, t)]),
          marker: { enabled: true, radius: 2 },
          lineWidth: 2,
        }))
      : [
          {
            name: METRICS.find((m) => m.key === metric)?.label || metric,
            data: timepoints.map((t) => [t, valueFor(metric, t)]),
            marker: { enabled: true, radius: 3 },
            lineWidth: 3,
          },
        ];

  const minX = timepoints[0] ?? daysAgo(rangeDays);
  const maxX = timepoints[timepoints.length - 1] ?? daysAgo(0, 23, 59);
  const currentLabel = METRICS.find((m) => m.key === metric)?.label || metric;

  const options = useMemo(
    () => ({
      chart: { type: "spline", height: 460, spacing: [8, 16, 16, 16] },
      title: {
        text:
          metric === "ALL"
            ? `All metrics — Last ${range === "24h" ? "24 hours" : "7 days"}`
            : `${currentLabel} — Last ${range === "24h" ? "24 hours" : "7 days"}`,
      },
      subtitle: {
        text: metric === "ALL" ? "Legend to hide/show series" : "Highcharts spline • Irregular time points",
      },
      time: { useUTC: false },
      xAxis: { type: "datetime", min: minX, max: maxX, tickInterval: 24 * 3600 * 1000, title: { text: "Date" } },
      yAxis: { title: { text: metric === "ALL" ? "Value" : currentLabel }, labels: { format: "{value}" } },
      tooltip: {
        xDateFormat: "%A, %e %b %Y, %H:%M",
        shared: metric === "ALL",
        pointFormat:
          metric === "ALL"
            ? '<span style="font-weight:600">{series.name}</span>: <b>{point.y:.2f}</b><br/>'
            : "<b>{point.y:.2f}</b>",
      },
      legend: { enabled: true },
      credits: { enabled: false },
      exporting: { enabled: false },
      series,
    }),
    [series, currentLabel, minX, maxX, metric, range]
  );

  // DataTables
  const tableRef = useRef(null);
  const dtRef = useRef(null);
  useEffect(() => {
    if (!tableRef.current) return;

    const columns = [
      { title: "Timestamp", data: "TIMESTAMP" },
      { title: "Epoch (ms)", data: "TIMESTAMP_EPOCH" },
      ...activeMetrics.map((m) => ({ title: m.label, data: m.key })),
    ];

    const data = timepoints.map((ts) => {
      const row = { TIMESTAMP: new Date(ts).toLocaleString(), TIMESTAMP_EPOCH: ts };
      activeMetrics.forEach((m) => (row[m.key] = valueFor(m.key, ts)));
      return row;
    });

    if (dtRef.current) {
      try { dtRef.current.destroy(); } catch {}
      dtRef.current = null;
    }
    tableRef.current.innerHTML = "";

    dtRef.current = new DataTable(tableRef.current, {
      data,
      columns,
      paging: true,
      pageLength: 25,
      responsive: true,
      ordering: true,
      order: [[1, "desc"]],
      searching: true,
      info: true,
      deferRender: true,
    });

    return () => {
      if (dtRef.current) {
        try { dtRef.current.destroy(); } catch {}
        dtRef.current = null;
      }
      if (tableRef.current) tableRef.current.innerHTML = "";
    };
  }, [timepoints, activeMetrics]);

  // CSV export
  const exportCSV = () => {
    const cols = ["Timestamp", "Epoch (ms)", ...activeMetrics.map((m) => m.label)];
    const header = cols.join(",");
    const lines = timepoints.map((ts) => {
      const base = [new Date(ts).toLocaleString(), ts];
      const vals = activeMetrics.map((m) => valueFor(m.key, ts));
      return [...base, ...vals].map((v, i) => (i === 0 ? `"${v}"` : String(v))).join(",");
    });
    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rd4_${metric === "ALL" ? "all" : metric}_${range}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-6xl space-y-6">
        {/* Header + Filters */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <h2 className="text-2xl font-semibold text-gray-800">Latest Data</h2>
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Metric : </span>
              <FancySelect
                value={metric}
                onChange={setMetric}
                options={METRICS.map((m) => ({ value: m.key, label: m.label }))}
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Range : </span>
              <FancySelect
                value={range}
                onChange={setRange}
                options={RANGE_OPTS}
              />
            </div>
            <button
              onClick={exportCSV}
              className="ml-auto inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm
                         font-medium text-gray-700 shadow-sm hover:bg-gray-50
                         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
              title="Download CSV"
            >
              Export CSV
            </button>
          </div>
        </div>

        {/* Metric Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {latestCards.map(({ key, label, value }) => (
            <div key={key} className="bg-white rounded-lg shadow p-4 hover:shadow-md transition">
              <div className="text-sm text-gray-500">{label}</div>
              <div className="mt-1 text-xl font-semibold text-gray-800">{Number(value).toFixed(2)}</div>
            </div>
          ))}
        </div>

        {/* Chart */}
        <div className="bg-white rounded-lg shadow p-4">
          <HighchartsReact highcharts={Highcharts} options={options} />
        </div>

        {/* DataTable */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="mb-3 text-lg font-medium text-gray-800">
            {metric === "ALL" ? "Recent readings — All metrics" : `Recent readings — ${currentLabel}`}
          </div>
          <div className="overflow-auto">
            <table ref={tableRef} className="display w-full text-sm" />
          </div>
        </div>
      </div>
    </div>
  );
}
